import { useCallback } from 'react';
import { cloneDeep, pick } from 'lodash';
import { toast } from 'react-toastify';
import {
  toCO2CalculatorInput,
  whitelistedPropsCO2Calculator,
} from 'pages/ProjectDetailsCalculator/RenovationHouseCalculator.data';
import { formatGraphqlErrors } from 'utils/helpers';
import { useMutation } from '@apollo/client/react';
import {
  type UpdateCO2CalculatorValuesMutationVariables,
  UpdateCO2CalculatorValuesDocument,
  CO2CalculationsDocument,
} from 'graphql/generated/graphql';
import type { TCO2CalculatorFormFields } from 'components/calculators/RenovationHouseForm/types';
import { messageOf } from 'utils/error';

export const useUpdateCO2CalculatorValuesHandler = ({ projectId }: { projectId: string }) => {
  const updateValues = useUpdateCO2CalculatorValues();
  const handleUpdate = useCallback(
    async (
      values: TCO2CalculatorFormFields,
      { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
    ) => {
      try {
        const newValues = cloneDeep(pick(values, whitelistedPropsCO2Calculator));
        const { energy_standard, is_solar_power_system } = newValues;
        if (energy_standard === 'passive_house' || energy_standard === 'low_energy_house') {
          newValues.renovations = [];
        }
        if (!is_solar_power_system) {
          newValues.solar_power_system_size = null;
          newValues.solar_power_system_consuming_percentage = null;
        }
        await updateValues({ projectId, formData: toCO2CalculatorInput(newValues) });
        window.scrollTo(0, 0);
      } catch (error) {
        toast.error(messageOf(error));
      } finally {
        setSubmitting(false);
      }
    },
    [projectId, updateValues],
  );
  return handleUpdate;
};

const useUpdateCO2CalculatorValues = () => {
  const [updateCO2CalculatorValuesMutation, { client }] = useMutation(
    UpdateCO2CalculatorValuesDocument,
  );

  const co2Calculator = useCallback(
    async (variables: UpdateCO2CalculatorValuesMutationVariables) => {
      try {
        const { data } = await updateCO2CalculatorValuesMutation({ variables });
        // Without errors (they throw) the non-null result is there.
        if (data) {
          client.writeQuery({
            query: CO2CalculationsDocument,
            variables: { projectId: variables.projectId },
            data: { co2Calculations: { ...data.updateCO2CalculatorValues } },
          });
        }
      } catch (error) {
        const e = formatGraphqlErrors(error);
        throw e;
      }
    },
    [client, updateCO2CalculatorValuesMutation],
  );
  return co2Calculator;
};

export default useUpdateCO2CalculatorValues;
